// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides a card component that displays a button to view a person's
 * audit log in a drawer. The audit log itself is loaded asynchronously when the
 * drawer is opened.
 */
import { PersonAuditList } from '@/account/components/person-audit-list';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import {
  CloseRounded,
  NavigateNextRounded,
} from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Suspense } from 'react';

interface Props {
  /**
   * The key of the person whose audit events should be displayed.
   */
  personKey: string;
}

/**
 * A card component that provides access to a person's audit log through
 * a drawer interface. The audit log is loaded asynchronously when the drawer
 * is opened.
 *
 * The component shows a simple card with a "View audit log" button. When clicked,
 * it opens a drawer from the right side of the screen containing the full audit log.
 * The drawer includes error handling and loading states.
 */
export function PersonAuditCard(props: Props) {
  const theme = useTheme();
  const drawer = useDisclosure();

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Drawer
        anchor="right"
        open={drawer.isOpen}
        onClose={drawer.close}
        PaperProps={{ sx: { maxWidth: '35rem', minWidth: '25rem' } }}
        sx={{ zIndex: theme.zIndex.modal }}
      >
        <Stack
          spacing={1}
          padding={2}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="subtitle1">Audit log</Typography>
          <IconButton size="small" onClick={drawer.close}>
            <CloseRounded fontSize="small" />
          </IconButton>
        </Stack>
        <Divider />
        <Box padding={2}>
          <SupportErrorBoundary message="An error occurred while loading the audit log.">
            <Suspense>
              <PersonAuditList personKey={props.personKey} />
            </Suspense>
          </SupportErrorBoundary>
        </Box>
      </Drawer>
      <Stack
        useFlexGap
        spacing={1}
        paddingX={2}
        paddingY={2}
        direction="row"
        alignItems="center"
      >
        <Typography variant="overline" marginRight="auto">
          History
        </Typography>
        <Button
          type="submit"
          size="small"
          onClick={drawer.open}
          endIcon={<NavigateNextRounded fontSize="small" />}
        >
          View audit log
        </Button>
      </Stack>
    </Paper>
  );
}
